import React, { useState } from 'react'
import axios from 'axios'
import { useCurrentUser } from 'contexts/CurrentUserContext'

const TrelloLinkPanel = ({ listId, list, onLinked }) => {
  const currentUser = useCurrentUser()
  const [showFlow, setShowFlow] = useState(false)
  const [boards, setBoards] = useState([])
  const [lists, setLists] = useState([])
  const [selectedBoardId, setSelectedBoardId] = useState('')
  const [selectedListId, setSelectedListId] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  if (list.trello_list_id) {
    return (
      <p className="text-muted mb-0">
        Linked to Trello: {list.trello_board_name} / {list.trello_list_name}
      </p>
    )
  }

  if (!currentUser?.trello_connected) {
    return (
      <p className="mb-0">
        <a href="/settings">Connect Trello</a> to link this list to a Trello board.
      </p>
    )
  }

  const handleApiError = (err) => {
    const data = err.response?.data
    if (data?.settings_path) {
      setError(
        <>
          {data.error}{' '}
          <a href={data.settings_path}>Go to Settings</a>
        </>
      )
    } else {
      setError(data?.error || 'Something went wrong.')
    }
  }

  const openFlow = () => {
    setShowFlow(true)
    setError(null)
    setLoading(true)

    axios.get('/api/trello/boards')
      .then(response => {
        setBoards(response.data)
        setLoading(false)
      })
      .catch(handleApiError)
  }

  const onBoardChange = (boardId) => {
    setSelectedBoardId(boardId)
    setSelectedListId('')
    setLists([])

    if (!boardId) return

    setLoading(true)
    axios.get(`/api/trello/boards/${boardId}/lists`)
      .then(response => {
        setLists(response.data)
        setLoading(false)
      })
      .catch(handleApiError)
  }

  const confirmLink = () => {
    const board = boards.find(b => b.id === selectedBoardId)
    const trelloList = lists.find(l => l.id === selectedListId)

    if (!board || !trelloList) return

    setLoading(true)
    setError(null)

    axios.post(`/api/lists/${listId}/trello_link`, {
      trello_link: {
        board_id: board.id,
        board_name: board.name,
        list_id: trelloList.id,
        list_name: trelloList.name
      }
    })
      .then(response => {
        onLinked(response.data)
        setShowFlow(false)
        setLoading(false)
      })
      .catch(handleApiError)
  }

  if (!showFlow) {
    return (
      <p className="mb-0">
        <button type="button" className="btn btn-link p-0 align-baseline" onClick={openFlow}>
          Link to Trello
        </button>
      </p>
    )
  }

  return (
    <div className="card mt-3">
      <div className="card-body">
        <h2 className="h6 card-title">Link to Trello</h2>

        {error && <div className="alert alert-danger">{error}</div>}

        <div className="mb-3">
          <label htmlFor="trello-board-select" className="form-label">Board</label>
          <select
            id="trello-board-select"
            className="form-select"
            value={selectedBoardId}
            onChange={(e) => onBoardChange(e.target.value)}
            disabled={loading}
          >
            <option value="">Select a board…</option>
            {boards.map(board => (
              <option key={board.id} value={board.id}>{board.name}</option>
            ))}
          </select>
        </div>

        {selectedBoardId && (
          <div className="mb-3">
            <label htmlFor="trello-list-select" className="form-label">List</label>
            <select
              id="trello-list-select"
              className="form-select"
              value={selectedListId}
              onChange={(e) => setSelectedListId(e.target.value)}
              disabled={loading}
            >
              <option value="">Select a list…</option>
              {lists.map(trelloList => (
                <option key={trelloList.id} value={trelloList.id}>{trelloList.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-primary"
            onClick={confirmLink}
            disabled={loading || !selectedBoardId || !selectedListId}
          >
            Import cards
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setShowFlow(false)}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

export default TrelloLinkPanel
